import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack,
  MenuItem, Select, InputLabel, FormControl, OutlinedInput, Box, Chip, Alert,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { rolesApi } from '../../api/roles.api';
import { lookupsApi } from '../../api/lookups.api';
import type { CreateUserInput, User } from '../../api/users.api';

interface Props {
  open: boolean;
  initial?: User | null;
  onClose: () => void;
  onSubmit: (input: CreateUserInput) => Promise<void>;
  error?: string | null;
}

const STATUSES = ['active', 'inactive', 'locked'];

export function UserFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const { data: roles = [] } = useQuery({ queryKey: ['roles'], queryFn: rolesApi.list });
  const { data: lookups } = useQuery({ queryKey: ['lookups'], queryFn: lookupsApi.all });
  const regions = lookups?.regions ?? [];
  const statesByRegion = new Map<number, string[]>();
  for (const s of lookups?.states ?? []) {
    if (s.regionId == null) continue;
    if (!statesByRegion.has(s.regionId)) statesByRegion.set(s.regionId, []);
    statesByRegion.get(s.regionId)!.push(s.name);
  }

  const [form, setForm] = useState<CreateUserInput>({ email: '', fullName: '', roleIds: [], status: 'active', regionIds: [] });
  const [submitting, setSubmitting] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        email: initial.email,
        fullName: initial.fullName,
        mobile: initial.mobile ?? undefined,
        status: initial.status,
        regionIds: initial.regionIds ?? (initial.regionId != null ? [initial.regionId] : []),
        roleIds: initial.roleIds,
      });
    } else {
      setForm({ email: '', fullName: '', roleIds: [], status: 'active', regionIds: [] });
    }
  }, [initial, open]);

  const set = <K extends keyof CreateUserInput>(k: K, v: CreateUserInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit User' : 'Create User'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            disabled={isEdit}
            required
            fullWidth
          />
          <TextField label="Full Name" value={form.fullName} onChange={(e) => set('fullName', e.target.value)} required fullWidth />
          <TextField label="Mobile" value={form.mobile ?? ''} onChange={(e) => set('mobile', e.target.value)} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Roles</InputLabel>
            <Select
              multiple
              value={form.roleIds}
              open={rolesOpen}
              onOpen={() => setRolesOpen(true)}
              onClose={() => setRolesOpen(false)}
              input={<OutlinedInput label="Roles" />}
              // Close the dropdown right after a pick so it's clear the role was selected.
              onChange={(e) => { set('roleIds', e.target.value as number[]); setRolesOpen(false); }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((id) => (
                    <Chip key={id} size="small" label={roles.find((r) => r.id === id)?.name ?? id} />
                  ))}
                </Box>
              )}
            >
              {roles.map((r) => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={form.status} onChange={(e) => set('status', e.target.value as CreateUserInput['status'])}>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Regions</InputLabel>
            <Select
              multiple
              value={form.regionIds ?? []}
              input={<OutlinedInput label="Regions" />}
              onChange={(e) => set('regionIds', e.target.value as number[])}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((id) => (
                    <Chip key={id} size="small" label={regions.find((r) => r.id === id)?.name ?? id} />
                  ))}
                </Box>
              )}
            >
              {regions.map((r) => {
                const states = statesByRegion.get(r.id) ?? [];
                return (
                  <MenuItem key={r.id} value={r.id}>
                    <Box>
                      <Box>{r.name}</Box>
                      {states.length > 0 && (
                        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{states.join(', ')}</Box>
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {/* Mapped states for the selected regions */}
          {(form.regionIds ?? []).length > 0 && (
            <Box sx={{ fontSize: 13, color: 'text.secondary', mt: -1 }}>
              {(form.regionIds ?? []).map((id) => {
                const name = regions.find((r) => r.id === id)?.name ?? `#${id}`;
                const states = statesByRegion.get(id) ?? [];
                return <div key={id}><b>{name}:</b> {states.length ? states.join(', ') : 'no states mapped'}</div>;
              })}
            </Box>
          )}
          {!isEdit && (
            <TextField
              label="Password (optional)"
              type="password"
              value={form.password ?? ''}
              onChange={(e) => set('password', e.target.value || undefined)}
              fullWidth
              helperText="Set a password manually (min 8 chars), or leave blank to auto-generate a temporary one."
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
